import { useState } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, AlertCircle, ArrowRight, Shield } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, ReferenceLine, AreaChart, Area } from 'recharts';
import { toast } from 'sonner';
import { exportRemediationRoadmapPDF } from '@/utils/exportUtils';

const moduleScores = [
  { name: 'Apex AI Trust', score: 71, color: '#F59E0B' },
  { name: 'AI Control Coverage', score: 82, color: '#10B981' },
  { name: 'Evidence & Compliance', score: 78, color: '#10B981' },
  { name: 'SOC 2 AI Coverage', score: 61, color: '#F59E0B' },
];

const trendData = [
  { date: 'Jan 15', score: 61 }, { date: 'Jan 22', score: 62 }, { date: 'Jan 29', score: 63 },
  { date: 'Feb 3', score: 69, event: 'CGVP deployment' }, { date: 'Feb 10', score: 68 },
  { date: 'Feb 17', score: 69 }, { date: 'Feb 24', score: 70 }, { date: 'Mar 3', score: 69 },
  { date: 'Mar 10', score: 70 }, { date: 'Mar 15', score: 74, event: 'EU AI Act gap sprint' },
  { date: 'Mar 22', score: 73 }, { date: 'Mar 29', score: 73 }, { date: 'Apr 5', score: 72 },
  { date: 'Apr 12', score: 73 }, { date: 'Apr 15', score: 74 },
];

const riskFactors = [
  { rank: 1, factor: 'GPT-4o Mini — unaudited, 0 compliance controls mapped', asset: 'GPT-4o Mini', severity: 'CRITICAL', impact: -8 },
  { rank: 2, factor: 'EU AI Act Transparency gaps — 3 models affected', asset: 'Claude Sonnet, GPT-4o Mini, Mistral 7B', severity: 'HIGH', impact: -5 },
  { rank: 3, factor: 'External Legal Corpus — unverified training dependency', asset: 'Claude Sonnet 3.7', severity: 'HIGH', impact: -4 },
  { rank: 4, factor: 'ISO 42001 certification incomplete — 12 gaps open', asset: 'All models', severity: 'MEDIUM', impact: -3 },
  { rank: 5, factor: 'CloudTrail logging gap — eu-west-1', asset: 'mglobal-legacy-apps', severity: 'MEDIUM', impact: -2 },
];

const distData = Array.from({ length: 40 }, (_, i) => {
  const x = 30 + i * 1.75;
  const mu = 61; const sigma = 12;
  const y = Math.exp(-0.5 * Math.pow((x - mu) / sigma, 2));
  return { score: Math.round(x), density: y };
});

const posturePie = [{ value: 74 }, { value: 26 }];

const CustomTooltip = ({ active, payload }) => {
  if (active && payload?.[0]) {
    const d = payload[0].payload;
    return (
      <div className="bg-[#0F1629] border border-[#1E2A45] rounded-lg px-3 py-2 text-xs shadow-xl">
        <div className="text-[#F8FAFC] font-medium">{d.date}</div>
        <div className="text-[#6366F1] font-mono">Score: {d.score}</div>
        {d.event && <div className="text-[#10B981] mt-1">{d.event}</div>}
      </div>
    );
  }
  return null;
};

const benchmarkData = {
  global: { group: 'Cross-jurisdictional peer group · regulated enterprises globally', median: 61, topQ: 84 },
  eu: { group: 'Regulated Financial Services & International Organizations', median: 61, topQ: 84 },
  na: { group: 'Regulated US Financial Services & Healthcare Organizations', median: 58, topQ: 81 },
  ca: { group: 'Canadian Federally Regulated Industries', median: 55, topQ: 79 },
};

export default function RiskScore() {
  const [benchRegion, setBenchRegion] = useState('global');
  const bench = benchmarkData[benchRegion];
  const aboveMedian = 74 - bench.median;
  const toTopQ = bench.topQ - 74;

  return (
    <div data-testid="risk-score-page" className="space-y-6">
      {/* Header */}
      <div className="text-sm text-[#64748B]">AI Security & Governance Posture — Meridian Global · Zurich Production</div>

      {/* Hero: Posture Score + Module Breakdown */}
      <div className="glass-card p-6 sm:p-8">
        <div className="flex flex-col md:flex-row items-center gap-8">
          {/* Donut chart */}
          <div className="relative w-48 h-48 flex-shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={posturePie} cx="50%" cy="50%" innerRadius={65} outerRadius={85} startAngle={90} endAngle={-270} dataKey="value" stroke="none">
                  <Cell fill="#6366F1" />
                  <Cell fill="#1E2A45" />
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className="text-4xl font-bold text-[#F8FAFC]">74</div>
              <div className="text-xs text-[#64748B]">out of 100</div>
            </div>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <TrendingUp size={16} className="text-[#10B981]" />
              <span className="text-sm text-[#10B981] font-medium">+3 points this week</span>
              <select data-testid="bench-region-selector" value={benchRegion} onChange={(e) => setBenchRegion(e.target.value)}
                className="ml-auto px-2 py-1 bg-[#0F1629] border border-[#1E2A45] rounded-lg text-[10px] text-[#94A3B8] focus:outline-none focus:border-[#6366F1]">
                <option value="global">Global</option>
                <option value="eu">EU</option>
                <option value="na">North America</option>
                <option value="ca">Canada</option>
              </select>
            </div>
            <div className="space-y-3 w-64">
              {moduleScores.map((m) => (
                <div key={m.name}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-[#94A3B8]">{m.name}</span>
                    <span className="text-xs font-mono font-bold" style={{ color: m.color }}>{m.score}/100</span>
                  </div>
                  <div className="h-2 rounded-full bg-[#1E2A45] overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${m.score}%`, backgroundColor: m.color }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Score Trend + Risk Factors */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Score Trend */}
        <div className="lg:col-span-3 glass-card p-6" data-testid="score-trend">
          <h3 className="text-sm font-semibold text-[#F8FAFC] mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Score Trend — 90 Days</h3>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trendData} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#475569' }} axisLine={{ stroke: '#1E2A45' }} tickLine={false} interval={2} />
                <YAxis domain={[50, 100]} tick={{ fontSize: 10, fill: '#475569' }} axisLine={{ stroke: '#1E2A45' }} tickLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <ReferenceLine x="Feb 3" stroke="#10B981" strokeDasharray="4 4" strokeWidth={1} />
                <ReferenceLine x="Mar 15" stroke="#10B981" strokeDasharray="4 4" strokeWidth={1} />
                <Line type="monotone" dataKey="score" stroke="#6366F1" strokeWidth={2.5} dot={false} activeDot={{ r: 4, fill: '#6366F1' }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="flex gap-6 mt-3 text-[10px] text-[#64748B]">
            <span><span className="inline-block w-2 h-2 rounded-full bg-[#10B981] mr-1" />Feb 3: CGVP deployment (+6 pts)</span>
            <span><span className="inline-block w-2 h-2 rounded-full bg-[#10B981] mr-1" />Mar 15: EU AI Act gap sprint (+4 pts)</span>
          </div>
        </div>

        {/* Top Risk Factors */}
        <div className="lg:col-span-2 glass-card p-6" data-testid="risk-factors">
          <h3 className="text-sm font-semibold text-[#F8FAFC] mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Top Risk Factors</h3>
          {/* Incident callout */}
          <div className="p-3 rounded-lg bg-[#080D1A] border-l-[3px] border-[#6366F1] mb-3" data-testid="incident-callout">
            <div className="text-xs text-[#F8FAFC] font-medium mb-1">Incident active: Legal Document Drafting</div>
            <div className="text-[10px] text-[#94A3B8]">Unauthorized change triggered investigation · Response package generated in <span className="text-[#10B981] font-bold">3 min 47 sec</span> · Evidence coverage maintained</div>
            <Link to="/dashboard/incident-review" className="text-[10px] text-[#6366F1] hover:text-[#818cf8] flex items-center gap-1 mt-1">View full investigation <ArrowRight size={10} /></Link>
          </div>
          <div className="space-y-3">
            {riskFactors.map((rf) => (
              <div key={rf.rank} className="flex items-start gap-3 py-2.5">
                <span className="text-[10px] font-mono text-[#475569] w-4 flex-shrink-0 mt-0.5">{rf.rank}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-[#F8FAFC] leading-relaxed mb-0.5">{rf.factor}</div>
                  <div className="flex items-center gap-3 text-[10px] font-mono">
                    <span style={{ color: rf.severity === 'CRITICAL' ? '#EF4444' : rf.severity === 'HIGH' ? '#F59E0B' : '#64748B' }}>{rf.severity}</span>
                    <span className="text-[#EF4444]">{rf.impact} pts</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Peer Benchmark */}
      <div className="glass-card p-6" data-testid="peer-benchmark">
        <h3 className="text-sm font-semibold text-[#F8FAFC] mb-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>How You Compare</h3>
        <div className="text-xs text-[#64748B] mb-5">{bench.group}</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          {[
            { label: 'Your Score', score: 74, color: '#6366F1', note: null },
            { label: 'Sector Median', score: bench.median, color: '#475569', note: `${aboveMedian} points above median`, noteColor: '#10B981' },
            { label: 'Top Quartile', score: bench.topQ, color: '#10B981', note: `${toTopQ} points to top quartile`, noteColor: '#F59E0B' },
          ].map((b) => (
            <div key={b.label} className="flex flex-col items-center p-4 rounded-lg bg-[#080D1A] border border-[#1E2A45]">
              <div className="relative w-20 h-20 mb-2">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={[{ v: b.score }, { v: 100 - b.score }]} cx="50%" cy="50%" innerRadius={28} outerRadius={36} startAngle={90} endAngle={-270} dataKey="v" stroke="none">
                      <Cell fill={b.color} />
                      <Cell fill="#1E2A45" />
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex items-center justify-center text-lg font-bold text-[#F8FAFC]">{b.score}</div>
              </div>
              <div className="text-xs text-[#94A3B8] mb-1">{b.label}</div>
              {b.note && <div className="text-[10px] text-center" style={{ color: b.noteColor }}>{b.note}</div>}
            </div>
          ))}
        </div>
        {/* Distribution chart */}
        <div className="h-28 relative">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={distData} margin={{ top: 0, right: 10, left: 10, bottom: 0 }}>
              <defs>
                <linearGradient id="distGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#475569" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#475569" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="score" tick={{ fontSize: 9, fill: '#475569' }} axisLine={{ stroke: '#1E2A45' }} tickLine={false} ticks={[40, 50, 61, 70, 74, 84, 90]} />
              <Area type="monotone" dataKey="density" stroke="#475569" strokeWidth={1.5} fill="url(#distGrad)" dot={false} />
              <ReferenceLine x={61} stroke="#475569" strokeDasharray="3 3" label={{ value: 'Median', position: 'top', fontSize: 9, fill: '#64748B' }} />
              <ReferenceLine x={74} stroke="#6366F1" strokeWidth={2} label={{ value: 'You', position: 'top', fontSize: 9, fill: '#6366F1', fontWeight: 'bold' }} />
              <ReferenceLine x={84} stroke="#10B981" strokeDasharray="3 3" label={{ value: 'Top 25%', position: 'top', fontSize: 9, fill: '#10B981' }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Remediation Impact */}
      <div className="glass-card p-6" data-testid="remediation-impact">
        <h3 className="text-sm font-semibold text-[#F8FAFC] mb-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Remediation Impact</h3>
        <div className="text-xs text-[#64748B] mb-5">If you close these gaps, your score improves to:</div>
        <div className="flex items-center gap-4 mb-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-[#F8FAFC]">74</div>
            <div className="text-[10px] text-[#64748B]">Current</div>
          </div>
          <div className="flex-1">
            <div className="relative h-4 rounded-full bg-[#1E2A45] overflow-hidden">
              <div className="absolute inset-y-0 left-0 rounded-full bg-[#6366F1]" style={{ width: '74%' }} />
              <div className="absolute inset-y-0 rounded-full bg-[#10B981]/40 border-r-2 border-[#10B981]" style={{ left: '74%', width: '15%' }} />
            </div>
            <div className="flex justify-between mt-1.5 text-[10px]">
              <span className="text-[#64748B]">0</span>
              <span className="text-[#10B981] font-bold">+15 points</span>
              <span className="text-[#64748B]">100</span>
            </div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-[#10B981]">89</div>
            <div className="text-[10px] text-[#64748B]">Projected</div>
          </div>
        </div>
        <div className="flex items-center justify-between flex-wrap gap-3 pt-4 border-t border-[#1E2A45]">
          <div className="text-xs text-[#94A3B8]">
            Estimated effort: <span className="text-[#F8FAFC] font-medium">3 sprints</span> · Primary owner: <span className="text-[#F8FAFC] font-medium">Compliance Team</span> · Target: <span className="text-[#F8FAFC] font-medium">Jul 31, 2026</span>
          </div>
          <button data-testid="generate-roadmap-btn" onClick={() => { exportRemediationRoadmapPDF(); toast.success('Remediation roadmap downloaded as PDF'); }} className="flex items-center gap-2 bg-[#6366F1] hover:bg-[#818cf8] text-white text-xs font-medium px-4 py-2.5 rounded-lg transition-all shadow-[0_0_8px_rgba(99,102,241,0.15)]">
            Generate remediation roadmap <ArrowRight size={13} />
          </button>
        </div>
      </div>

      {/* What Moves Your Score */}
      <div className="glass-card p-6" data-testid="what-moves-score">
        <h3 className="text-sm font-semibold text-[#F8FAFC] mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>What Moves Your Score</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className="text-xs font-semibold text-[#10B981] uppercase tracking-wider mb-3">Actions that increase your score</div>
            <div className="space-y-2.5">
              {[
                { action: 'Complete GPT-4o Mini audit', pts: '+8 pts' },
                { action: 'Remediate EU AI Act Art.13 gaps', pts: '+5 pts' },
                { action: 'Verify External Legal Corpus dependency', pts: '+4 pts' },
                { action: 'Complete ISO 42001 certification', pts: '+3 pts' },
                { action: 'Enable CloudTrail eu-west-1', pts: '+2 pts' },
              ].map((item) => (
                <div key={item.action} className="flex items-center gap-2 text-xs">
                  <TrendingUp size={12} className="text-[#10B981] flex-shrink-0" />
                  <span className="text-[#94A3B8] flex-1">{item.action}</span>
                  <span className="text-[#10B981] font-mono font-bold text-[11px]">{item.pts}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs font-semibold text-[#6366F1] uppercase tracking-wider mb-3">What protects your score</div>
            <div className="space-y-2.5">
              {[
                'Evidence Vault integrity monitoring',
                'Continuous compliance control mapping',
                'Agent permission governance',
              ].map((item) => (
                <div key={item} className="flex items-center gap-2 text-xs">
                  <Shield size={12} className="text-[#6366F1] flex-shrink-0" />
                  <span className="text-[#94A3B8]">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
